import { useState } from 'react';
import { Anchor, Button, Group, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useAddInvestorsToDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useAddInvestorsToDocument';
import { AddInvestorModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddInvestor/AddInvestorModal';
import {
	REFUSAL_COPY,
	STRUCTURAL_REFUSALS,
	type AddInvestorRefusalCode,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddInvestor/AddInvestorModal.types';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	onAdded: () => void;
}

const isKnownRefusal = (reason: string): reason is AddInvestorRefusalCode => reason in REFUSAL_COPY;

// Fallback for a reason the frontend's hand-maintained union hasn't caught up to yet — the
// button must never go quietly disabled with no stated cause.
const UNAVAILABLE_FALLBACK_COPY = "This action isn't available for this document right now.";

export const AddInvestorAction = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onAdded,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const { eligibility, addInvestors } = useAddInvestorsToDocument({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});

	const rawReason = eligibility.data?.reason;
	const refusal = rawReason && isKnownRefusal(rawReason) ? rawReason : null;
	const canAdd = eligibility.data?.canAdd ?? false;

	const handleSubmit = async (investorIds: string[]) => {
		await addInvestors.mutateAsync({
			path: { dealId, programmeId, documentWorkflowInstanceId },
			body: { investorIds },
		});
		setIsOpen(false);
		onAdded();
	};

	// Nothing to offer and nothing to explain: the eligibility query hasn't resolved (or 403'd for a
	// viewer), or the document structurally cannot take an investor. Every document in the product
	// renders this component, so those cases must leave no trace.
	if (!eligibility.data || (rawReason && STRUCTURAL_REFUSALS.has(rawReason))) {
		return null;
	}

	// The action isn't allowed but the manager can act on it — always show *some* explanation: a
	// known refusal's exact copy, or a neutral fallback for a reason the union hasn't caught up to.
	const explanation = canAdd
		? null
		: (refusal && REFUSAL_COPY[refusal]) || UNAVAILABLE_FALLBACK_COPY;

	return (
		<Stack gap="xs" align="flex-start">
			<Button
				variant="default"
				onClick={() => setIsOpen(true)}
				disabled={!canAdd}
				data-testid={TEST_ELEMENT_IDS.ADD_INVESTOR_ACTION_BUTTON}
			>
				Add investor
			</Button>

			{explanation && (
				<Group gap={4} wrap="nowrap">
					<Text
						size="0.75rem"
						c="gray.6"
						data-testid={TEST_ELEMENT_IDS.ADD_INVESTOR_REFUSAL_MESSAGE}
					>
						{explanation}
					</Text>
					{refusal === 'SIGNATURE_HEADROOM_EXHAUSTED' && (
						<Anchor
							component={Link}
							size="0.75rem"
							to={mipManagePath(MIP_MANAGE_ROUTES.DOCUMENTS, {
								dealId,
								mipId: programmeId,
							})}
							data-testid={TEST_ELEMENT_IDS.ADD_INVESTOR_REISSUE_LINK}
						>
							Re-issue the document instead
						</Anchor>
					)}
				</Group>
			)}

			<AddInvestorModal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				eligibleInvestors={eligibility.data.eligibleInvestors}
				freeRows={eligibility.data.freeRows}
				totalRows={eligibility.data.totalRows}
				isSubmitting={addInvestors.isPending}
				onSubmit={handleSubmit}
			/>
		</Stack>
	);
};
